import React, { useState, useEffect, useCallback, useRef } from 'react';
import { notificationService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { 
  AlertTriangle, 
  X, 
  Key,
  Calendar,
  ChevronRight,
  CreditCard,
  Lock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * LİSANS UYARI SİSTEMİ
 * ====================
 * 
 * UYARI TAKVİMİ:
 * - 30 gün kala: Günde 1 kez uyarı (kapatılabilir, ertesi gün tekrar)
 * - 15 gün kala: Günde 1 kez uyarı (kapatılabilir, ertesi gün tekrar)
 * - Son 7 gün: Günde 3-4 kez uyarı (kapatılabilir ama sık çıkar)
 * - Son gün: Tüm kullanıcılara uyarı + sistem kilitleme uyarısı
 * - Süresi doldu: Tam ekran kilit, sadece ödeme sayfası erişilebilir
 * 
 * UYARI ALICILARI:
 * - Site Manager varsa: Site Manager'a gider
 * - Bireysel kullanıcı: Kullanıcının kendisine gider
 * - Super Admin: Her zaman bilgilendirilir
 */

// LocalStorage key
const DISMISSED_WARNINGS_KEY = 'hims_dismissed_license_warnings_v2';

// Bugünün tarih key'i
const getTodayKey = () => new Date().toISOString().split('T')[0];

// Günün hangi çeyreğinde olduğumuzu hesapla (0-3)
const getDayQuarter = () => {
  const hour = new Date().getHours();
  if (hour < 6) return 0;
  if (hour < 12) return 1;
  if (hour < 18) return 2;
  return 3;
};

// Kalan gün sayısına göre günlük maksimum uyarı sayısı
const getMaxDailyWarnings = (daysRemaining) => {
  if (daysRemaining <= 1) return 999; // Son gün: sınırsız
  if (daysRemaining <= 7) return 4;   // Son 7 gün: günde 4 kez
  return 1;                            // 30-8 gün: günde 1 kez
};

export default function LicenseWarningBanner() {
  const { user, isAdmin, isSiteManager, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [warnings, setWarnings] = useState([]);
  const [dismissedData, setDismissedData] = useState({ ids: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const checkIntervalRef = useRef(null);

  // LocalStorage'dan kapatılan uyarıları yükle
  useEffect(() => {
    try {
      const stored = localStorage.getItem(DISMISSED_WARNINGS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const today = getTodayKey();
        
        // Eski günlerin verilerini temizle
        if (parsed.date !== today) {
          setDismissedData({ ids: [], counts: {}, date: today });
          localStorage.setItem(DISMISSED_WARNINGS_KEY, JSON.stringify({ ids: [], counts: {}, date: today }));
        } else {
          setDismissedData(parsed);
        }
      }
    } catch (e) {
      console.error('Failed to load dismissed warnings:', e);
    }
  }, []);

  const fetchLicenseWarnings = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const notifications = await notificationService.list(false, 50);
      const licenseWarnings = (notifications || []).filter(
        n => n.type === 'license_warning'
      );
      setWarnings(licenseWarnings);
    } catch (err) {
      console.error('Failed to fetch license warnings:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchLicenseWarnings();
    
    // Her 15 dakikada bir kontrol et (son 7 gün için sık uyarılar)
    checkIntervalRef.current = setInterval(() => {
      fetchLicenseWarnings();
    }, 15 * 60 * 1000); // 15 dakika
    
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [fetchLicenseWarnings]);

  const handleDismiss = async (warning) => {
    const daysRemaining = warning.data?.days_remaining || 0;
    const licenseId = warning.data?.license_id;
    const today = getTodayKey();
    const quarter = getDayQuarter();
    const dismissKey = `${licenseId}_${today}_${quarter}`;
    
    // Kapatma sayısını artır
    const currentCount = dismissedData.counts[`${licenseId}_${today}`] || 0;
    const maxWarnings = getMaxDailyWarnings(daysRemaining);
    
    const newCounts = {
      ...dismissedData.counts,
      [`${licenseId}_${today}`]: currentCount + 1
    };
    
    const newData = {
      ids: [...dismissedData.ids, dismissKey],
      counts: newCounts,
      date: today
    };
    
    setDismissedData(newData);
    localStorage.setItem(DISMISSED_WARNINGS_KEY, JSON.stringify(newData));
    
    // 30 günden fazla ise backend'de de okundu işaretle
    if (daysRemaining > 30) {
      try {
        await notificationService.markAsRead(warning.id);
      } catch (err) {
        console.error('Failed to mark as read:', err);
      }
    }
  };

  const handleGoToLicenses = () => {
    navigate('/licenses');
  };

  // Uyarıları filtrele
  const visibleWarnings = warnings.filter(w => {
    const daysRemaining = w.data?.days_remaining || 0;
    const licenseId = w.data?.license_id;
    const today = getTodayKey();
    const quarter = getDayQuarter();
    
    // 30 günden fazla: Gösterme (ilk 30 gün uyarısı kaldırıldı)
    if (daysRemaining > 30) {
      return false;
    }
    
    // Süresi dolmuş: Her zaman göster (kilit ekranı)
    if (daysRemaining <= 0) {
      return true;
    }
    
    // Günlük kapatma sayısını kontrol et
    const dismissCount = dismissedData.counts[`${licenseId}_${today}`] || 0;
    const maxWarnings = getMaxDailyWarnings(daysRemaining);
    
    // Günlük limit aşıldıysa gösterme
    if (dismissCount >= maxWarnings) {
      return false;
    }
    
    // Son 7 gün: Çeyrek bazlı kontrol (günde 4 kez)
    if (daysRemaining <= 7) {
      const quarterKey = `${licenseId}_${today}_${quarter}`;
      return !dismissedData.ids.includes(quarterKey);
    }
    
    // 30-8 gün: Günlük kontrol
    const dayKey = `${licenseId}_${today}_0`; // Sadece ilk çeyreği kontrol et
    return !dismissedData.ids.includes(dayKey);
  });

  // Kullanıcı rolüne göre filtreleme
  const userRole = user?.role;
  const isNormalUser = userRole === 'user';
  
  let filteredWarnings = visibleWarnings;
  
  if (isNormalUser) {
    // Normal kullanıcı: Sadece son gün olanları göster
    filteredWarnings = visibleWarnings.filter(w => {
      const daysRemaining = w.data?.days_remaining || 0;
      return daysRemaining <= 1;
    });
  }

  if (loading || filteredWarnings.length === 0) {
    return null;
  }

  // SÜRESİ DOLMUŞ LİSANS - TAM EKRAN KİLİT
  const expiredLicenses = filteredWarnings.filter(w => {
    const daysRemaining = w.data?.days_remaining;
    return daysRemaining !== undefined && daysRemaining <= 0;
  });

  if (expiredLicenses.length > 0) {
    const expiredLicense = expiredLicenses[0];
    const licenseKey = expiredLicense.data?.license_key || 'Bilinmiyor';
    const siteName = expiredLicense.data?.site_name || 'Genel';
    const price = expiredLicense.data?.price || 0;
    const currency = expiredLicense.data?.currency || 'TRY';
    const expiresAt = expiredLicense.data?.expires_at;
    const currencySymbols = { USD: '$', EUR: '€', TRY: '₺', GBP: '£' };
    const symbol = currencySymbols[currency] || '₺';

    return (
      <div 
        className="fixed inset-0 z-[9999] bg-gradient-to-br from-red-900 via-red-800 to-red-900 flex items-center justify-center p-4"
        data-testid="license-expired-blocker"
      >
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-600 to-red-700 p-8 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10 bg-red-900"></div>
            
            <div className="relative">
              <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <Lock className="h-14 w-14 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-white mb-2">
                SİSTEM KİLİTLENDİ
              </h1>
              <p className="text-red-100 text-lg">
                Lisans süreniz dolmuştur
              </p>
            </div>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-4">
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wide">Lisans Anahtarı</p>
                  <p className="font-mono font-bold text-gray-900">{licenseKey}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wide">Site</p>
                  <p className="font-medium text-gray-900">{siteName}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wide">Bitiş Tarihi</p>
                  <p className="font-medium text-red-600">
                    {expiresAt ? new Date(expiresAt).toLocaleDateString('tr-TR') : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs uppercase tracking-wide">Yenileme Ücreti</p>
                  <p className="font-bold text-2xl text-emerald-600">
                    {symbol}{price.toLocaleString('tr-TR')}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-semibold mb-1">Sistem Erişimi Kısıtlandı</p>
                  <p>Lisansınızı yenileyene kadar sistemi kullanamazsınız. Ödeme yaptıktan sonra anında erişim sağlanacaktır.</p>
                </div>
              </div>
            </div>
            
            {/* Payment Button */}
            <button
              onClick={() => navigate(`/licenses/${expiredLicense.data?.license_id}/payment`, { 
                state: { 
                  license: {
                    id: expiredLicense.data?.license_id,
                    license_key: licenseKey,
                    price: price,
                    currency: currency,
                    expires_at: expiresAt,
                    site_name: siteName
                  }
                }
              })}
              className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold py-4 px-6 rounded-xl text-lg transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
              data-testid="pay-now-button"
            >
              <CreditCard className="h-6 w-6" />
              Kredi Kartı ile Öde
            </button>
            
            <div className="text-center">
              <button
                onClick={() => navigate('/licenses')}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Lisans detaylarını görüntüle
              </button>
            </div>
            
            <p className="text-xs text-gray-400 text-center">
              Kredi kartı ile anında, Havale/EFT ile onay sonrası aktif olur.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // NORMAL UYARI BANNER'I
  return (
    <div className="fixed top-0 left-0 right-0 z-[100]" data-testid="license-warning-banner">
      {filteredWarnings.map((warning, index) => {
        const daysRemaining = warning.data?.days_remaining || 0;
        const licenseKey = warning.data?.license_key || 'Bilinmiyor';
        const siteName = warning.data?.site_name || 'Genel';
        const expiresAt = warning.data?.expires_at;
        const price = warning.data?.price || 0;
        const currency = warning.data?.currency || 'TRY';
        
        // Aciliyet renklerini belirle
        let bgColor = 'bg-amber-500';
        let borderColor = 'border-amber-600';
        let textColor = 'text-white';
        let pulseClass = '';
        let urgencyText = '';
        
        if (daysRemaining <= 1) {
          bgColor = 'bg-red-600';
          borderColor = 'border-red-700';
          pulseClass = 'animate-pulse';
          urgencyText = '🚨 SON GÜN!';
        } else if (daysRemaining <= 3) {
          bgColor = 'bg-red-500';
          borderColor = 'border-red-600';
          pulseClass = 'animate-pulse';
          urgencyText = '⚠️ ACİL';
        } else if (daysRemaining <= 7) {
          bgColor = 'bg-orange-500';
          borderColor = 'border-orange-600';
          urgencyText = '⏰ Acele Edin';
        } else if (daysRemaining <= 15) {
          bgColor = 'bg-amber-500';
          borderColor = 'border-amber-600';
        }

        const currencySymbols = { USD: '$', EUR: '€', TRY: '₺', GBP: '£' };
        const symbol = currencySymbols[currency] || '₺';

        // Son 7 günde günde kaç uyarı gösterileceği bilgisi
        const maxWarnings = getMaxDailyWarnings(daysRemaining);
        const showFrequencyNote = daysRemaining <= 7 && daysRemaining > 0;

        return (
          <div
            key={warning.id}
            className={`${bgColor} ${borderColor} border-b-4 ${textColor} shadow-lg ${pulseClass}`}
            data-testid={`license-warning-${warning.id}`}
          >
            <div className="max-w-7xl mx-auto px-4 py-3 lg:ml-64">
              <div className="flex items-center justify-between flex-wrap gap-2">
                {/* Warning Icon and Message */}
                <div className="flex items-center gap-3 flex-1">
                  <div className="p-2 rounded-full bg-white/20">
                    <AlertTriangle className="h-6 w-6" />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {urgencyText && (
                        <span className="px-2 py-0.5 rounded bg-white/30 text-sm font-bold">
                          {urgencyText}
                        </span>
                      )}
                      <span className="font-bold text-lg">
                        Lisans Süresi: <span className="text-2xl">{daysRemaining}</span> gün kaldı!
                      </span>
                      <span className="px-2 py-0.5 rounded text-sm font-medium bg-black/20">
                        {siteName !== 'Bilinmiyor' ? siteName : licenseKey}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm mt-1 opacity-90 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Key className="h-4 w-4" />
                        {licenseKey}
                      </span>
                      {expiresAt && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          Bitiş: {new Date(expiresAt).toLocaleDateString('tr-TR')}
                        </span>
                      )}
                      {price > 0 && (
                        <span className="font-semibold">
                          Yenileme: {symbol}{price.toLocaleString('tr-TR')}
                        </span>
                      )}
                      {showFrequencyNote && (
                        <span className="text-xs opacity-75 italic">
                          (Günde {maxWarnings} kez hatırlatılacak)
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate(`/licenses/${warning.data?.license_id}/payment`, { 
                      state: { 
                        license: {
                          id: warning.data?.license_id,
                          license_key: licenseKey,
                          price: price,
                          currency: currency,
                          expires_at: expiresAt,
                          max_stations: warning.data?.max_stations
                        }
                      }
                    })}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-bold transition-colors bg-white text-gray-900 hover:bg-gray-100 shadow-lg"
                    data-testid="pay-button"
                  >
                    <CreditCard className="h-4 w-4" />
                    Ödeme Yap
                  </button>
                  
                  <button
                    onClick={handleGoToLicenses}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg font-medium transition-colors bg-white/20 hover:bg-white/30"
                  >
                    Detaylar
                    <ChevronRight className="h-4 w-4" />
                  </button>
                  
                  <button
                    onClick={() => handleDismiss(warning)}
                    className="p-2 rounded-lg transition-colors hover:bg-white/20"
                    title={daysRemaining <= 7 
                      ? `Şimdilik kapat (bugün ${maxWarnings - (dismissedData.counts[`${warning.data?.license_id}_${getTodayKey()}`] || 0)} kez daha gösterilecek)` 
                      : "Bugün için kapat"}
                    data-testid={`dismiss-warning-${warning.id}`}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
